<template>
  <div>
    <!-- Popup/Modal for validation errors and feedback -->
    <div v-if="showPopup" class="popup-overlay" @click.self="closePopup">
      <div class="popup-card">
        <div class="popup-icon-title">
          <span v-if="popupType==='warning'" class="popup-icon warning">⚠️</span>
          <span v-else class="popup-icon info">ℹ️</span>
          <span class="popup-title">{{ popupTitle }}</span>
        </div>
        <div class="popup-message">
          <span v-for="(line, idx) in popupMessage.split('\n')" :key="idx">{{ line }}<br v-if="idx < popupMessage.split('\n').length-1"/></span>
        </div>
        <button class="popup-btn" @click="closePopup">{{ $t('pages.AddHouseRoom.got_it_5b8027f') }}</button>
      </div>
    </div>

    <!-- Main Layout -->
    <div class="outer_box">
      <div class="all_out_box">
        <div class="tttiltleBox">
          <div class="title-container">
            <h1 class="main-title">
              <span class="icon-title">🚪</span> {{ $t('pages.AddHouseRoom.add_a_new_room_349da31') }}
            </h1>
            <div class="title-underline"></div>
            <p class="subtitle">{{ fromEditReview ? 'Add a unit to this building — we will attach it to your review.' : 'Add a new room to this property' }}</p>
          </div>
        </div>

        <!-- House Info (read-only) -->
        <div class="top_rate" v-if="houseName">
          <div class="top_rate_right">
            <div class="form-section">
              <h3 class="section-title"><span class="section-icon">🏠</span> {{ $t('pages.AddHouseRoom.property_9ae33a7') }}</h3>
              <div class="house-info-display">
                <span class="house-name">{{ houseName }}</span>
                <span class="house-address" v-if="houseAddress">{{ houseAddress }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Room Information -->
        <div class="tmp_rate">
          <div class="rateBlock">
            <h3 class="section-title"><span class="section-icon">🚪</span> {{ $t('pages.AddHouseRoom.room_information_48daf6c') }}</h3>
            
            <!-- Search existing rooms first -->
            <div class="search-section">
              <label class="form-label">{{ $t('pages.AddHouseRoom.search_for_existing_room_fef943d') }}</label>
              <el-select 
                v-model="searchQuery2" 
                class="elselect"
                filterable
                remote
                :remote-method="prehandle2"
                :loading="loading" 
                :placeholder="$t('pages.AddHouseRoom.type_to_find_your_room_ee10746')"
                @change="handleSelectChange2"
              >
                <el-option
                  key="manual-room"
                  :label="$t('pages.AddHouseRoom.input_manually_726a1dd')"
                  value="__manual_room__">
                </el-option>
                <el-option
                  v-for="item in searchResults2"
                  :key="item.houseLetId"
                  :label="item.houseLetName"
                  :value="item.houseLetId">
                </el-option>
              </el-select>
              <p class="input-helper">{{ $t('pages.AddHouseRoom.try_finding_the_room_if_it_isn_t_l_df482ef') }}</p>
              <button
                type="button"
                class="manual-input-btn"
                @click="enterManualMode"
                v-if="!showManualRoomForm && !selectedHouseLetId"
              >{{ $t('pages.AddHouseRoom.input_manually_new_floor_new_room_e2870be') }}</button>
            </div>
            
            <div class="input-group" v-if="showManualRoomForm || selectedHouseLetId">
              <div class="input-field full-width">
                <label class="form-label">{{ $t('pages.AddHouseRoom.property_type_ad794a4') }}</label>
                <div class="button-container">
                  <button
                    v-for="type in propertyTypes"
                    :key="type"
                    :class="{'active': room.propertyType === type, 'modern-btn': true}"
                    @click="selectPropertyType(type)"
                    class="bed-type-button"
                  >
                    {{ type }}
                  </button>
                </div>
              </div>

              <template v-if="room.propertyType === 'Village House'">
                <div class="input-field">
                  <label class="form-label">{{ $t('pages.AddHouseRoom.floor_7db82f7') }}</label>
                  <div class="input-container">
                    <input v-model="room.floor" type="text" :placeholder="$t('pages.AddHouseRoom.e_g_g_f_1_f_5455941')" class="custom-input modern-input" />
                  </div>
                </div>
                <div class="input-field">
                  <label class="form-label">{{ $t('pages.AddHouseRoom.room_90e2fca') }}</label>
                  <div class="input-container">
                    <input v-model="room.droom" type="text" :placeholder="$t('pages.AddHouseRoom.e_g_rooma_roomb_a3f6e21')" class="custom-input modern-input" />
                  </div>
                </div>
              </template>

              <template v-if="room.propertyType === 'Estate'">
                <div class="input-field">
                  <label class="form-label">Block</label>
                  <div class="input-container">
                    <input v-model="room.block" type="text" placeholder="e.g. Block 8" class="custom-input modern-input" />
                  </div>
                </div>
                <div class="input-field">
                  <label class="form-label">Floor</label>
                  <div class="input-container">
                    <input v-model="room.floor" type="text" placeholder="e.g. G/F, 1/F" class="custom-input modern-input" />
                  </div>
                </div>
                <div class="input-field">
                  <label class="form-label">Unit/Flat</label>
                  <div class="input-container">
                    <input v-model="room.droom" type="text" placeholder="e.g. Flat C" class="custom-input modern-input" />
                  </div>
                </div>
              </template>

              <template v-if="room.propertyType === 'Apartment/Building'">
                <div class="input-field">
                  <label class="form-label">Floor</label>
                  <div class="input-container">
                    <input v-model="room.floor" type="text" placeholder="e.g. G/F, 1/F" class="custom-input modern-input" />
                  </div>
                </div>
                <div class="input-field">
                  <label class="form-label">Unit/Flat</label>
                  <div class="input-container">
                    <input v-model="room.droom" type="text" placeholder="e.g. Flat C" class="custom-input modern-input" />
                  </div>
                </div>
              </template>

              <template v-if="room.propertyType === 'Dorm'">
                <div class="input-field">
                  <label class="form-label">Floor</label>
                  <div class="input-container">
                    <input v-model="room.floor" type="text" placeholder="e.g. G/F, 1/F" class="custom-input modern-input" />
                  </div>
                </div>
                <div class="input-field">
                  <label class="form-label">Room</label>
                  <div class="input-container">
                    <input v-model="room.droom" type="text" placeholder="e.g. Room 302, Hall A" class="custom-input modern-input" />
                  </div>
                </div>
              </template>

              <template v-if="room.propertyType === 'Studio'">
                <div class="input-field">
                  <label class="form-label">Floor</label>
                  <div class="input-container">
                    <input v-model="room.floor" type="text" placeholder="e.g. G/F, 1/F" class="custom-input modern-input" />
                  </div>
                </div>
                <div class="input-field">
                  <label class="form-label">Unit / Studio</label>
                  <div class="input-container">
                    <input v-model="room.droom" type="text" placeholder="e.g. S12" class="custom-input modern-input" />
                  </div>
                </div>
              </template>

              <template v-if="room.propertyType === 'Other'">
                <div class="input-field">
                  <label class="form-label">Floor</label>
                  <div class="input-container">
                    <input v-model="room.floor" type="text" placeholder="e.g. G/F, 1/F" class="custom-input modern-input" />
                  </div>
                </div>
                <div class="input-field">
                  <label class="form-label">Room / Unit</label>
                  <div class="input-container">
                    <input v-model="room.droom" type="text" placeholder="e.g. Room A" class="custom-input modern-input" />
                  </div>
                </div>
              </template>
            </div>
          </div>
        </div>

        <div class="reviewBlock">
          <div class="newb">
            <button @click="submitRoom" class="submit-button modern-btn">
              <span class="button-text">ADD ROOM</span>
              <span class="button-icon">→</span>
            </button>
            <p class="agreement-text">
              By clicking "ADD ROOM", I confirm that I have read and agree to Sparrow's
              <a href="#/terms-of-use#appendix-b" target="_blank" rel="noopener" class="agreement-link">Community Guidelines</a>,
              <a href="#/terms-of-use" target="_blank" rel="noopener" class="agreement-link">Terms of Use</a>, and
              <a href="#/privacy-policy" target="_blank" rel="noopener" class="agreement-link">Privacy Policy</a>.
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'AddHouseRoom',
  data() {
    return {
      houseId: 0,
      houseName: '',
      houseAddress: '',
      loading: false,
      searchQuery2: '',
      searchResults2: [],
      selectedHouseLetId: 0,
      showManualRoomForm: false,
      room: {
        propertyType: '',
        propertyName: '',
        block: '',
        floor: '',
        unit: '',
        droom: ''
      },
      propertyTypes: ['Village House', 'Estate', 'Apartment/Building', 'Dorm', 'Studio', 'Other'],
      showPopup: false,
      popupMessage: '',
      popupTitle: '',
      popupType: 'warning',
      fromEditReview: false,
      returnReviewId: ''
    };
  },
  computed: {
    propertyNameLabel() {
      const labels = {
        'Village House': 'Village Name',
        'Estate': 'Estate Name',
        'Apartment/Building': 'Building Name',
        'Dorm': 'Dorm Name',
        'Studio': 'Studio Name',
        'Other': 'Property Name'
      };
      return labels[this.room.propertyType] || 'Property Name';
    },
    propertyNamePlaceholder() {
      const placeholders = {
        'Village House': 'e.g. Sai Kung Village',
        'Estate': 'e.g. Tseung Kwan O Plaza',
        'Apartment/Building': 'e.g. Ocean View Court',
        'Dorm': 'e.g. HKUST Hall 1',
        'Studio': 'e.g. CityLiving Studios',
        'Other': 'Enter property name'
      };
      return placeholders[this.room.propertyType] || 'Enter property name';
    }
  },
  created() {
    this.houseId = parseInt(this.$route.query.houseId) || 0;
    this.fromEditReview = this.$route.query.from === 'editReview';
    this.returnReviewId = this.$route.query.reviewId ? String(this.$route.query.reviewId) : '';
    if (this.houseId) {
      this.fetchHouseInfo();
      this.fetchExistingRooms();
    }
  },
  methods: {
    fetchHouseInfo() {
      const obj = this;
      axios.get('/api/house', {
        params: { houseId: this.houseId },
        timeout: 10000
      })
      .then(function (response) {
        const data = response.data.data;
        if (data) {
          obj.houseName = data.name || '';
          obj.houseAddress = [data.street, data.address, data.detailedArea, data.area, data.district, data.region].filter(Boolean).join(', ');
        }
      })
      .catch(function (error) {
        console.error('Failed to fetch house info:', error);
      });
    },
    fetchExistingRooms() {
      const obj = this;
      axios.get('/api/search/room', {
        params: {
          keyword: ' ',
          houseId: this.houseId,
          pageNo: 1,
          pageSize: 50
        },
        timeout: 10000
      })
      .then(function (response) {
        obj.searchResults2 = response.data.data || [];
      })
      .catch(function (error) {
        console.error('Failed to fetch rooms:', error);
        obj.searchResults2 = [];
      });
    },
    prehandle2(query) {
      const obj = this;
      axios.get('/api/search/room', {
        params: {
          keyword: query,
          houseId: this.houseId,
          pageNo: 1,
          pageSize: 10
        },
        timeout: 10000
      })
      .then(function (response) {
        obj.searchResults2 = response.data.data || [];
      })
      .catch(function (error) {
        console.error(error);
        obj.searchResults2 = [];
      });
    },
    handleSelectChange2(value) {
      if (value === '__manual_room__') {
        this.selectedHouseLetId = 0;
        this.showManualRoomForm = true;
        this.searchQuery2 = '';
        return;
      }
      this.selectedHouseLetId = value;
      this.showManualRoomForm = false;
    },
    selectPropertyType(type) {
      this.room.propertyType = type;
    },
    enterManualMode() {
      this.selectedHouseLetId = 0;
      this.showManualRoomForm = true;
      this.searchQuery2 = '';
    },
    submitRoom() {
      const errors = [];

      if (!this.selectedHouseLetId && !this.showManualRoomForm) {
        errors.push('Please select an existing room or choose "Input manually"');
      }

      if (this.showManualRoomForm) {
        if (!this.room.propertyType) {
          errors.push('Property Type is required');
        } else {
          if (this.room.propertyType === 'Village House') {
            if (!this.room.floor.trim()) errors.push('Floor is required for Village House');
            if (!this.room.droom.trim()) errors.push('Room is required for Village House');
          } else if (this.room.propertyType === 'Estate') {
            if (!this.room.block.trim()) errors.push('Block is required for Estate');
            if (!this.room.floor.trim()) errors.push('Floor is required for Estate');
            if (!this.room.droom.trim()) errors.push('Unit/Flat is required for Estate');
          } else if (this.room.propertyType === 'Apartment/Building') {
            if (!this.room.floor.trim()) errors.push('Floor is required for Apartment/Building');
            if (!this.room.droom.trim()) errors.push('Unit/Flat is required for Apartment/Building');
          } else if (this.room.propertyType === 'Dorm' || this.room.propertyType === 'Studio' || this.room.propertyType === 'Other') {
            if (!this.room.floor.trim()) errors.push(`Floor is required for ${this.room.propertyType}`);
            if (!this.room.droom.trim()) errors.push(`Room/Unit is required for ${this.room.propertyType}`);
          }
        }
      }

      if (errors.length > 0) {
        this.popupTitle = 'Please Complete Required Fields';
        this.popupType = 'warning';
        this.popupMessage = errors.join('\n');
        this.showPopup = true;
        return;
      }

      this.addRoom();
    },
    addRoom() {
      const obj = this;
      axios.post('/api/house/add', {
        houseId: this.houseId,
        houseLetId: this.selectedHouseLetId || 0,
        name: this.houseName,
        type: '',
        region: '',
        district: '',
        area: '',
        detailedArea: '',
        street: '',
        address: '',
        houseLet: '',
        propertyType: this.room.propertyType,
        floor: this.room.floor,
        room: this.room.droom,
        block: this.room.block
      }, { timeout: 10000 })
      .then(function (response) {
        const resp = response.data.data;
        if (resp) {
          if (obj.fromEditReview && obj.returnReviewId) {
            obj.$router.push({
              path: '/editReview',
              query: {
                id: obj.returnReviewId,
                houseLetId: resp.houseLetId
              }
            });
            return;
          }
          obj.$router.push({
            path: '/rateTheHouse',
            query: {
              houseId: resp.houseId,
              houseLetId: resp.houseLetId,
              ifFirst: resp.ifFirst
            }
          });
        }
      })
      .catch(function (error) {
        console.error('Failed to add room:', error);
        const status = error?.response?.status;
        const body = error?.response?.data;
        if (status === 409 && body?.error === 'DUPLICATE_ROOM' && body?.existingHouseLetId) {
          // Room already exists — navigate straight to it
          const existingId = body.existingHouseLetId;
          if (obj.fromEditReview && obj.returnReviewId) {
            obj.$router.push({
              path: '/editReview',
              query: { id: obj.returnReviewId, houseLetId: existingId }
            });
            return;
          }
          obj.$router.push({
            path: '/rateTheHouse',
            query: { houseId: obj.houseId, houseLetId: existingId, ifFirst: false }
          });
          return;
        }
        obj.popupTitle = 'Error';
        obj.popupType = 'warning';
        obj.popupMessage = 'Failed to add room. Please try again.';
        obj.showPopup = true;
      });
    },
    closePopup() {
      this.showPopup = false;
    }
  }
};
</script>

<style scoped>
.outer_box {
  background: rgba(60, 194, 255, 1);
  min-height: 100vh;
  padding: 0 0 48px 0;
  display: flex;
  justify-content: center;
  align-items: flex-start;
}
.all_out_box {
  width: 100%;
  max-width: 920px;
  margin: 0 auto;
  background: #fff;
  border-radius: 24px;
  box-shadow: 0 20px 60px 0 rgba(59, 194, 255, 0.37);
  padding: 24px 36px 36px 36px;
  display: flex;
  flex-direction: column;
  align-items: stretch;
}
.tttiltleBox { margin-bottom: 36px; }
.title-container { text-align: center; }
.main-title {
  font-size: 2.4rem;
  font-weight: 700;
  color: #3bc2ff;
  margin-bottom: 12px;
  display: inline-block;
}
.icon-title { font-size: 2.2rem; margin: 0 4px; }
.title-underline {
  width: 80px;
  height: 4px;
  border-radius: 4px;
  background: linear-gradient(90deg, #3bc2ff, #00e0d0);
  margin: 0 auto 12px auto;
}
.subtitle {
  color: #64748b;
  font-size: 1.08rem;
  margin: 0;
}
.form-section, .rateBlock {
  margin-bottom: 28px;
}
.section-title {
  font-size: 1.25rem;
  font-weight: 700;
  color: #3bc2ff;
  margin-bottom: 18px;
  letter-spacing: 0.5px;
}
.section-icon { margin-right: 8px; }
.house-info-display {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 16px 20px;
  background: linear-gradient(135deg, #f0f9ff, #e0f2fe);
  border-radius: 12px;
  border: 1px solid #bae6fd;
}
.house-name {
  font-size: 1.15rem;
  font-weight: 600;
  color: #0c4a6e;
}
.house-address {
  font-size: 0.9rem;
  color: #64748b;
}
.search-section {
  margin-bottom: 20px;
}
.form-label {
  display: block;
  font-weight: 600;
  color: #334155;
  margin-bottom: 8px;
  font-size: 0.95rem;
}
.elselect {
  width: 100%;
}
.manual-input-btn {
  margin-top: 10px;
  padding: 10px 18px;
  border: 1.5px dashed #3CC2FF;
  background: #f0faff;
  color: #1d8acb;
  border-radius: 10px;
  font-weight: 600;
  cursor: pointer;
  font-size: 0.95rem;
  transition: background 0.15s, color 0.15s;
}
.manual-input-btn:hover {
  background: #3CC2FF;
  color: #fff;
}
.input-helper {
  font-size: 0.82rem;
  color: #94a3b8;
  margin-top: 6px;
}
.input-group {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}
.input-field { margin-bottom: 12px; }
.input-field.full-width {
  grid-column: 1 / -1;
}
.input-container { position: relative; }
.custom-input.modern-input {
  width: 100%;
  padding: 12px 16px;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  font-size: 1rem;
  transition: border-color 0.2s;
  outline: none;
  box-sizing: border-box;
}
.custom-input.modern-input:focus {
  border-color: #3bc2ff;
}
.button-container {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}
.bed-type-button {
  padding: 10px 22px;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  background: #fff;
  font-size: 0.95rem;
  font-weight: 500;
  color: #334155;
  cursor: pointer;
  transition: all 0.2s;
}
.bed-type-button:hover {
  border-color: #3bc2ff;
  color: #3bc2ff;
}
.bed-type-button.active {
  background: #3bc2ff;
  color: #fff;
  border-color: #3bc2ff;
}
.reviewBlock {
  margin-top: 20px;
}
.newb {
  text-align: center;
}
.submit-button {
  padding: 16px 48px;
  background: linear-gradient(135deg, #3bc2ff, #00e0d0);
  color: #fff;
  border: none;
  border-radius: 14px;
  font-size: 1.15rem;
  font-weight: 700;
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;
  box-shadow: 0 8px 24px rgba(59, 194, 255, 0.35);
}
.submit-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 32px rgba(59, 194, 255, 0.45);
}
.button-text { margin-right: 8px; }
.button-icon { font-size: 1.2rem; }
.agreement-text {
  margin-top: 16px;
  font-size: 0.82rem;
  color: #94a3b8;
}
.agreement-link {
  color: #3bc2ff;
  text-decoration: none;
}
/* Popup */
.popup-overlay {
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0,0,0,0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}
.popup-card {
  background: #fff;
  border-radius: 18px;
  padding: 32px 28px 24px;
  max-width: 420px;
  width: 90%;
  box-shadow: 0 20px 60px rgba(0,0,0,0.18);
  text-align: center;
  max-height: 85vh;
  overflow-y: auto;
}

@media (max-width: 480px) {
  .popup-card {
    padding: 20px 16px 16px;
    border-radius: 14px;
    width: 95%;
  }
  
  .popup-title {
    font-size: 1rem;
  }
  
  .popup-message {
    font-size: 0.9rem;
    line-height: 1.5;
  }
  
  .popup-btn {
    padding: 8px 20px;
    font-size: 0.95rem;
  }
}
.popup-icon-title {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin-bottom: 16px;
}
.popup-icon { font-size: 1.8rem; }
.popup-title {
  font-size: 1.2rem;
  font-weight: 700;
  color: #1e293b;
}
.popup-message {
  font-size: 0.95rem;
  color: #475569;
  line-height: 1.6;
  margin-bottom: 20px;
  text-align: left;
}
.popup-btn {
  padding: 10px 36px;
  background: #3bc2ff;
  color: #fff;
  border: none;
  border-radius: 10px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
}
.popup-btn:hover {
  background: #2aa8e6;
}

/* Responsive */
@media (max-width: 768px) {
  .all_out_box {
    margin: 0 16px;
    padding: 24px 16px;
  }
  .input-group {
    grid-template-columns: 1fr;
  }
  .main-title {
    font-size: 1.6rem;
  }
}
</style>
